import { useParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

import {
  GeneratePDFParams,
  useProjectDetails,
} from "pages/Details/hooks/useProjectDetails";
import { Project } from "types";
import { DocType } from "components/Documents/types";
import { BasePage } from "components/BasePage/BasePage";
import { Directive } from "pages/Backoffice/Questionaire/types";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { zipDownload } from "components/FileExplorer/FileExplorer";
import { DocumentTable } from "components/Documents/DocumentTable";
import { usableDocs } from "components/Documents/data/docDefinitions";
import { DocumentLibrary } from "components/Documents/DocumentLibrary";
import { requiredDocsMapping } from "components/Documents/data/requiredDocsMapping";
import { useEnhancedDocuments } from "components/Documents/hooks/useEnhancedDocuments";
import { GenerateDocumentsModal } from "components/Documents/DocumentLibrary/GenerateDocumentsModal";

interface RiskRouteParams {
  projectId: string;
}

export const getLatestVersionDocs = (project?: Project) => {
  if (!project) {
    return [];
  }

  if (!project.versions?.length) {
    return project.documents || [];
  }

  const sortedVersions = project.versions?.sort(
    (a, b) =>
      new Date(b.createdAt).getMilliseconds() -
      new Date(a.createdAt).getMilliseconds()
  );

  const latestVersion = sortedVersions?.[0];

  return latestVersion?.documents || [];
};

export const hasRenderableDocs = (project?: Project) => {
  if (!project) {
    return false;
  }

  const requiredDocuments = requiredDocsMapping[project.projectdata.category];
  if (!requiredDocuments?.length) {
    return false;
  }

  return requiredDocuments.every((doc) => !!usableDocs[doc]?.completeFn(project));
};

export const Documents = () => {
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeatureFlags();
  const { projectId } = useParams<RiskRouteParams>();
  const { project, fetchInProgress, updateVersion, updateInProgress } =
    useProjectDetails(projectId);

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [selected, setSelected] = useState<DocType[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [downloadable, setDownloadable] = useState(false);

  const renderable = hasRenderableDocs(project);
  const docs = useEnhancedDocuments(project);

  const isBackwardsCompatibility =
    isFeatureEnabled(Feature.DocVersioning) && !project?.versions?.length;

  useEffect(() => {
    if (project) {
      setSelectedLanguages(project.languages.map((lang) => lang.code));
    }
  }, [project]);

  const handleSelect = useCallback(
    (docs: DocType[]) => {
      setSelected(docs);
      setDownloadable(
        docs.length > 0 &&
          docs.every((d) =>
            getLatestVersionDocs(project).find((pd) => pd.type === d)
          )
      );
    },
    [project]
  );

  const handleDownload = useCallback(() => {
    if (!project) return;

    zipDownload(
      project.documents.filter((d) => selected.includes(d.type as DocType)),
      projectId,
      project.projectdata.product_name
    );
  }, [project, projectId, selected]);

  const handleGenerator = useCallback(
    (params?: GeneratePDFParams) => {
      if (!project || !renderable) return;

      updateVersion({
        ...params,
        isUserGenerated: isFeatureEnabled(Feature.DocVersioning),
        selectedDocs: params?.selectedDocs || (selected.length ? selected : []),
        selectedLanguages: params?.selectedLanguages || selectedLanguages,
      });
    },
    [
      project,
      renderable,
      updateVersion,
      selected,
      selectedLanguages,
      isFeatureEnabled,
    ]
  );

  const isAnyOtherThanMrlExit =
    project?.directives.some((dir) => dir.name !== Directive.mrl) &&
    project?.projectdata.category === "exit";

  const category = isAnyOtherThanMrlExit
    ? "exit_" + project.directives[0].name
    : project?.projectdata.category;

  return (
    <BasePage
      breadcrumbItems={[
        { label: "projects", location: "/dashboard" },
        {
          label: project?.projectdata.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: "steps.documents.label",
          location: `/projects/${projectId}/documents`,
        },
      ]}
      loading={fetchInProgress}
    >
      <div>
        <div className="pb-5 ">
          <h3 className="text-3xl leading-6 font-medium text-gray-900">
            {t("documents.label")}
            <span className="inline text-sm pl-2 text-cyan-600 cursor-pointer">
              Wiki
            </span>
          </h3>

          <p className="text-sm text-gray-500 block pt-8 w-1/2">
            <Trans
              i18nKey="documents.subline"
              values={{
                category: t(`common.selector.product_category.${category}`),
              }}
            />
          </p>
        </div>
      </div>

      {(!isFeatureEnabled(Feature.DocVersioning) ||
        !project?.versions?.length) && (
        <div className="pt-2 text-right space-x-2 flex items-center justify-end">
          <PrimaryButton onClick={handleDownload} disabled={!downloadable}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 mr-1"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25"
              />
            </svg>
            {t("documents.download.load_selected")}
          </PrimaryButton>

          <PrimaryButton
            onClick={
              isBackwardsCompatibility
                ? () => setGenerateModalOpen(true)
                : handleGenerator
            }
            disabled={
              updateInProgress || !renderable || selectedLanguages.length === 0
            }
          >
            {updateInProgress
              ? t("documents.download.generate_progress") +
                ` ${project!.projectdata.version + 1}...`
              : t("documents.download.generate_revision")}
          </PrimaryButton>
        </div>
      )}

      <div>
        {project?.versions && isFeatureEnabled(Feature.DocVersioning) ? (
          <DocumentLibrary
            project={project}
            onSelect={handleSelect}
            isLoading={updateInProgress}
            onGenerate={handleGenerator}
          />
        ) : (
          <DocumentTable project={project} onSelect={handleSelect} />
        )}
      </div>

      {isFeatureEnabled(Feature.DocVersioning) && isBackwardsCompatibility && (
        <GenerateDocumentsModal
          isOpen={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          onGenerate={handleGenerator}
          selectedDocs={docs.map((d) => d.type)}
          selectedLanguages={project?.languages?.map((lang) => lang.code) || []}
          projectName={project?.projectdata.product_name}
          project={project}
          previousRevisionKey={""}
        />
      )}
    </BasePage>
  );
};
